import React, { useState, useRef, useEffect, useMemo } from "react";
import { TextField, Autocomplete, Switch, FormControlLabel, Box, Typography } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DatePicker } from "@mui/x-date-pickers";
import fr from "date-fns/locale/fr";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import CarLogo from "./CarLogo";
import { useTranslation } from 'react-i18next';

const DynamicField = ({
  fieldType,
  label,
  options,
  value,
  onChange,
  variant = "outlined",
  type = "text",
  format,
  formatNumber,
  isMtn,
  isInt,
  isNumber,
  isDouble,
  acceptNegative = false,
  error,
  helperText,
  length = 100,
  autofocus,
  inputRef,
  optionName,
  handleOptions,
  secondOptionName,
  separator,
  row,
  id,
  name,
  required,
  tabIndex,
  disabled,
  groupBy,
  params = {},

  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false); // eslint-disable-next-line
  const [selectedValue, setSelectedValue] = useState(null);
  const textFieldRef = useRef(null);
  const { i18n } = useTranslation();
  const isRtl = i18n.language?.startsWith('ar');
  // const inputStyles = {
  //     '& .MuiOutlinedInput-root': {
  //         backgroundColor: disabled ? 'rgba(0, 0, 0, 0.12)' : 'var(--body-grey) !important',
  //         '&:hover': {
  //             backgroundColor: disabled ? 'rgba(0, 0, 0, 0.12)' : 'var(--body-grey)',
  //             '& .MuiOutlinedInput-notchedOutline': {
  //                 borderColor: 'rgba(0, 0, 0, 0.23)',
  //             }
  //         },
  //         '&.Mui-focused': {
  //             backgroundColor: 'var(--body-grey)',
  //             '& .MuiOutlinedInput-notchedOutline': {
  //                 borderColor: disabled ? 'rgba(0, 0, 0, 0.23)' : 'primary.main',
  //             }
  //         },
  //     },
  //     '& .MuiFilledInput-root': {
  //         backgroundColor: disabled ? 'rgba(0, 0, 0, 0.12)' : 'var(--body-grey)',
  //         '&:hover': {
  //             backgroundColor: disabled ? 'rgba(0, 0, 0, 0.12)' : 'var(--body-grey)',
  //         },
  //         '&.Mui-focused': {
  //             backgroundColor: 'var(--body-grey)',
  //         }
  //     }
  // };

  useEffect(() => {
    if (autofocus && textFieldRef.current && !isFocused) {
      textFieldRef.current.focus();
      setIsFocused(true);
    }
  }, [autofocus, isFocused]);

  const handleTextFieldChange = (e) => {
    let inputValue = e?.target?.value || "";

    if (isMtn) {
      const mtnRegex = /^-?\d+(\.\d{0,2})?$/;

      if (inputValue === "" || mtnRegex.test(inputValue)) {
        onChange(inputValue);
      } else {
        console.warn("Montant invalide");
      }
    } else if (type === "text" || type === "password") {
      onChange(inputValue);
    } else if (type === "number") {
      const numberRegex = /^-?\d*(\.\d+)?$/;
      if (numberRegex) {
        onChange(inputValue);
      }
    } else if (isInt) {
      const IntRegex = /^[0-9]*$/;
      if (IntRegex) {
        onChange(inputValue);
      } else {
        console.warn("Nombre invalide");
      }
    } else if (isDouble) {
      const DoubleRegex = /^-?\d*(\.\d{0,10})?$/;
      if (DoubleRegex) {
        onChange(inputValue);
      } else {
        console.warn("Nombre invalide");
      }
    } else if (type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex) {
        onChange(inputValue);
      } else {
        console.warn("Adresse e-mail invalide");
      }
    } else if (type === "tel") {
      const phoneRegex = /^[0][5-7][4-9][0-9]{7}$/;
      if (phoneRegex) {
        onChange(inputValue);
      } else {
        console.warn("Numéro de téléphone invalide");
      }
    }

    e.preventDefault();
  };

  const handleKeyDown = (e) => {
    if (isMtn) {
      const allowedKeys = [
        "Backspace",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
        ".",
        "-",
        "+",
        "Tab",
        "Enter",
      ];

      if (e.key.toLowerCase() === "e") {
        if (e.target.value.includes("e") || e.target.value.includes("E")) {
          e.preventDefault();
        }
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "v" ||
          e.key === "c" ||
          e.key === "z" ||
          e.key === "a" ||
          e.key === "x")
      ) {
        return;
      }
      if (!/\d/.test(e.key) && !allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    } else if (isInt) {
      const allowedKeys = [
        "Backspace",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
        "Tab",
        "Enter",
      ];
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "v" ||
          e.key === "c" ||
          e.key === "z" ||
          e.key === "a" ||
          e.key === "x")
      ) {
        return;
      }
      if (!/^[0-9]*$/.test(e.key) && !allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    } else if (isDouble) {
      const allowedKeys = [
        "Backspace",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
        "Tab",
        "Enter",
        ".",
        "-",
      ];
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "v" ||
          e.key === "c" ||
          e.key === "z" ||
          e.key === "a" ||
          e.key === "x")
      ) {
        return;
      }
      if (
        !/^-?[0-9]*([.,][0-9]{0,10})?$/.test(e.key) &&
        !allowedKeys.includes(e.key)
      ) {
        // if (!/\d/.test(e.key) && !allowedKeys.includes(e.key)) {

        e.preventDefault();
      }
    }
  };

  // const handleKeyDown = (e) => {
  //     const commonAllowedKeys = [
  //         "Backspace",
  //         "ArrowLeft",
  //         "ArrowRight",
  //         "Delete",
  //         "Tab",
  //         "Enter",
  //     ];

  //     const isShortcutKey = (e.ctrlKey || e.metaKey) && ["v", "c", "z", "a", "x"].includes(e.key);

  //     if (isShortcutKey) return; // Permet les raccourcis clavier

  //     if (isMtn) {
  //         const mtnAllowedKeys = [...commonAllowedKeys, ".", "-", "+"];
  //         if (e.key.toLowerCase() === "e" && /e/i.test(e.target.value)) {
  //             e.preventDefault();
  //         } else if (!/\d/.test(e.key) && !mtnAllowedKeys.includes(e.key)) {
  //             e.preventDefault();
  //         }
  //     } else if (isInt) {
  //         if (!/^\d$/.test(e.key) && !commonAllowedKeys.includes(e.key)) {
  //             e.preventDefault();
  //         }
  //     } else if (isDouble) {
  //         const doubleAllowedKeys = [...commonAllowedKeys, ".", "-"];
  //         if (!/^-?\d*(\.\d{0,10})?$/.test(e.key) && !doubleAllowedKeys.includes(e.key)) {
  //             e.preventDefault();
  //         }
  //     }
  // };

  // Normaliser les options à partir de carLogosData
  const carOptions = useMemo((options) => {
    if (!options || !Array.isArray(options)) return [];

    return options.map(item => ({
      value: item.name,
      label: item.name,
      slug: item.slug,
      image: item.image,
      // Pour la recherche
      searchText: `${item.name} ${item.slug}`.toLowerCase()
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  // Trouver l'option sélectionnée
  const selectedCarOption = useMemo(() => {
    if (!value) return null;
    return carOptions.find(opt =>
      opt.value.toLowerCase() === String(value).toLowerCase() ||
      opt.label.toLowerCase() === String(value).toLowerCase()
    ) || {
      value: value,
      label: value,
    };
  }, [value, carOptions]);

  switch (fieldType) {
    case "textfield":

      return (
        <TextField
          fullWidth
          label={label}
          value={value || ''}
          id={id}
          name={name}
          required={required}
          onChange={handleTextFieldChange}
          variant={variant}
          type={type}
          error={error}
          helperText={helperText}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          autoFocus={autofocus}
          // inputProps={{
          //   maxLength: type === "tel" ? 10 : length,
          //   pattern:
          //     type === "tel"
          //       ? "[0][5-7][4-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9]"
          //       : isMtn
          //         ? "^-?[0-9]*([.,][0-9]{0,2})?$"
          //         : isInt
          //           ? "/^[0-9]*$/"
          //           : isDouble
          //             ? "^-?[0-9]*([.,][0-9]{0,10})?$"
          //             : undefined,
          //   ...params.inputProps,
          //   tabIndex: tabIndex,
          //   autoComplete: "new-password",

          // }}

          inputRef={(input) => {
            if (inputRef) {
              inputRef(input);
            }
            textFieldRef.current = input;
            if (input) {
              input.onfocus = () => input.select();
            }
          }}
          {...props}
          inputProps={{
            maxLength: type === "tel" ? 10 : length,
            pattern:
              type === "tel"
                ? "[0][5-7][4-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9]"
                : isMtn
                  ? "^-?[0-9]*([.,][0-9]{0,2})?$"
                  : isInt
                    ? "/^[0-9]*$/"
                    : isDouble
                      ? "^-?[0-9]*([.,][0-9]{0,10})?$"
                      : undefined,
            ...params.inputProps,
            ...(props.inputProps || {}),
            tabIndex: tabIndex,
            autoComplete: "off",
            dir: isRtl ? "rtl" : "ltr",
          }}
          sx={{
            ...props.sx,
            "& .MuiInputBase-input": {
              textAlign: isRtl ? "right" : "left",
              direction: isRtl ? "rtl" : "ltr",
            },
            "& .MuiInputLabel-root": {
               transformOrigin: isRtl ? "right" : "left",
          right: isRtl ? "1.75rem" : "unset",
          left: isRtl ? "unset" : undefined
            },
            "& .MuiOutlinedInput-notchedOutline legend": {
              textAlign: isRtl ? "right" : "left",
            },
            "& .css-1p0jlq3-MuiFormLabel-root-MuiInputLabel-root": {
              fontSize: "0.9rem !important",
            }
          }}

        />
      );

    case "autocomplete":
      const { renderValue: customRenderValue, ...autocompleteProps } = props;
      return (
        <Autocomplete
          options={options || []}
          getOptionKey={(option) => option.id}
          groupBy={groupBy}
          getOptionLabel={(option) => {
            const sep = separator || ": ";

            if (secondOptionName && option[secondOptionName] !== "") {
              if (sep === "[]") {
                return `${option[optionName]} [${option[secondOptionName]}]`;
              } else if (sep === " ") {
                return option[optionName] !== "vide"
                  ? `${option[optionName]} ${option[secondOptionName]}`
                  : option[secondOptionName];
              } else {
                return `${option[optionName]}${sep}${option[secondOptionName]}`;
              }
            } else {
              return option[optionName] !== "vide" ? option[optionName] : "";
            }
          }}
          isOptionEqualToValue={(option, value) => {
            // Gestion des valeurs nulles ou undefined
            if (!option || !value) return false;

            // Si les deux ont un ID, compare par ID
            if (option.id && value.id) return option.id === value.id;

            // Sinon compare par le nom principal
            return option[optionName] === value[optionName];
          }}

          value={value || null}
          onChange={(e, newValue) => {
            e.preventDefault();
            setSelectedValue(newValue?.id || "");
            onChange(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant={variant}
              required={required}
              autoFocus={autofocus}
              disabled={disabled}
              id={id}
              name={name || id}
              autoComplete="off"
              inputProps={{
                ...params.inputProps,
                autoComplete: "off",
                tabIndex: tabIndex,
                ...(customRenderValue && value ? { style: { opacity: 0, position: 'absolute' } } : {}),
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: customRenderValue && value
                  ? customRenderValue(value)
                  : params.InputProps?.startAdornment,
              }}
              inputRef={(input) => {
                textFieldRef.current = input;
                if (input) {
                  input.onfocus = () => input.select();
                }
              }}
              sx={{
                ...props.sx,
                "& .css-1p0jlq3-MuiFormLabel-root-MuiInputLabel-root": {
                  fontSize: "0.9rem !important",
                }
              }} />
          )}
          disabled={disabled}
          {...autocompleteProps}
        />
      );

    case "carLogo":
      const { renderValue: customRenderValueCar, ...carLogoProps } = props;

      const logosData = options || [];
      const carOptions = Array.isArray(logosData) && logosData.length > 0
        ? logosData.map(item => ({
          value: item.name,
          label: item.name,
          slug: item.slug,
          image: item.image,
          searchText: `${item.name} ${item.slug}`.toLowerCase()
        })).sort((a, b) => a.label.localeCompare(b.label))
        : [];

      // Calcul direct sans useMemo (pas de hook dans le case)
      const getSelectedCarOption = () => {
        if (!value) return null;

        // Si c'est déjà une string libre (valeur personnalisée)
        if (typeof value === 'string') {
          // Chercher uniquement une correspondance exacte
          const found = carOptions.find(opt =>
            opt.value.toLowerCase() === value.toLowerCase()
          );
          return found || value; // ← retourner la STRING directement si pas trouvé
        }

        if (typeof value === 'object' && value !== null && value.value) {
          const found = carOptions.find(opt =>
            opt.value.toLowerCase() === value.value.toLowerCase()
          );
          return found || value.value; // ← string directe
        }

        return null;
      };

      const selectedCarOption = getSelectedCarOption();

      return (
        <Autocomplete
          value={selectedCarOption}
          onChange={(e, newValue) => {
            e.preventDefault();
            if (newValue === null) {
              onChange(null);
            } else {
              onChange(newValue);
            }
          }}
          onInputChange={(e, newInputValue, reason) => {
            if (reason === 'input') {
              onChange(newInputValue);
            } else if (reason === 'clear') {
              onChange(null);
            }
          }}
          freeSolo
          forcePopupIcon={true}
          options={carOptions || []}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option?.label || '';
          }}
          isOptionEqualToValue={(option, val) => {
            if (!option || !val) return false;
            if (typeof option === 'string' && typeof val === 'string') return option === val;
            if (typeof option === 'string') return option === val?.value;
            if (typeof val === 'string') return option.value === val;
            return option.value === val?.value;
          }}
          filterOptions={(options, state) => {
            const searchValue = state.inputValue.toLowerCase().trim();
            if (!searchValue) return options;
            return options.filter(option =>
              option.searchText.includes(searchValue) ||
              option.label.toLowerCase().includes(searchValue)
            );
          }}
          loading={carOptions.length === 0}
          loadingText="Chargement des marques..."
          noOptionsText=""
          renderOption={(props, option) => {
            if (typeof option === 'string') {
              const { key, ...optionProps } = props;
              return (
                <li key={option} {...optionProps}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {option}
                    </Typography>
                  </Box>
                </li>
              );
            }
            const { key, ...optionProps } = props;
            return (
              <li key={option.value} {...optionProps}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <CarLogo
                    brand={option.value}
                    size={32}
                    variant="thumb"
                    showPlaceholder={true}
                    logosData={logosData}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {option.label}
                    </Typography>
                  </Box>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant={variant}
              required={required}
              autoFocus={autofocus}
              disabled={disabled}
              inputRef={inputRef}
              id={id}
              name={name || id}
              onClick={(e) => e.stopPropagation()}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  paddingLeft: '8px',
                }
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedCarOption?.value && carOptions.find(opt => opt.value === selectedCarOption.value) ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    <CarLogo
                      brand={selectedCarOption.value}
                      size={24}
                      variant="thumb"
                      logosData={logosData}
                    />
                  </Box>
                ) : null,
              }}
            />
          )}
          sx={{
            width: '100%',
            '& .MuiAutocomplete-groupLabel': {
              backgroundColor: '#f5f5f5',
              fontWeight: 600,
              fontSize: '0.875rem',
              padding: '8px 16px',
              width: '100%',
            }
          }}
          {...carLogoProps}
        />
      );

    case "datepicker":
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
          <DatePicker
            format="dd-MM-yyyy"
            label={label}
            value={value || null}
            onChange={onChange}
            disabled={disabled}
            sx={{
              "& .MuiButtonBase-root": {
                marginRight: "0px !important",
                tabIndex: -1
              },
              "& .MuiInputBase-root": { color: "" },
              ...props.sx,
              "& .css-1p0jlq3-MuiFormLabel-root-MuiInputLabel-root": {
                fontSize: "0.9rem !important",
              },
              "& .MuiInputAdornment-root": {
                marginLeft: "0px !important",
                marginRight: "10px !important",
              },
            }}
            slotProps={{
              textField: {
                inputProps: {
                  tabIndex: tabIndex,
                },
                inputRef: (input) => {
                  textFieldRef.current = input;
                  if (input) {
                    input.onfocus = () => input.select();
                  }
                }
              },
              openPickerButton: {
                tabIndex: -1
              }
            }}
            {...props}
          />
        </LocalizationProvider>
      );

    case "timepicker":
      const { onBlur, minTime, maxTime, error: timeError, sx: timeSx, ...restProps } = props;

      return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
          <Box
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                onBlur?.();
              }
            }}
          >
            <TimePicker
              label={label}
              value={value}
              onChange={onChange}
              onClose={onBlur}
              minTime={minTime}
              maxTime={maxTime}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={timeError || false}
                  helperText={timeError ? "Heure invalide" : ""}
                />
              )}
              {...restProps}
              sx={{
                ...timeSx,
                ...(timeError && {
                  "& .MuiOutlinedInput-root": {
                    borderColor: "#f44336",
                    "&:hover": { borderColor: "#f44336" },
                  },
                }),
                "& .css-1p0jlq3-MuiFormLabel-root-MuiInputLabel-root": {
                  fontSize: "0.9rem !important",
                },
              }}
            />
          </Box>
        </LocalizationProvider>
      );
    case "switch":
      return label ? (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              id={id}
              name={name}
              {...props}
            />
          }
          label={label}
        />
      ) : (
        <Switch
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          id={id}
          name={name}
          {...props}
        />
      );
    default:
      return null;
  }
};

export default DynamicField;
